import { Context, Effect, Layer, Schema } from "effect";
import { resourceUnavailable, type ResourceFailure } from "@workerstation/shared/errors/resource";
export const RECENT_FLOW_SCOPE_LIMIT = 8;
export const RecentFlowScope = Schema.Struct({
    workspaceId: Schema.String.check(Schema.isMinLength(1)),
    scopeId: Schema.String.check(Schema.isMinLength(1)),
    seenAt: Schema.Finite
});
export type RecentFlowScope = typeof RecentFlowScope.Type;
export class RecordingScopeIo extends Context.Service<RecordingScopeIo, {
    readonly read: Effect.Effect<string | null, ResourceFailure>;
    readonly write: (value: string) => Effect.Effect<void, ResourceFailure>;
    readonly now: Effect.Effect<number, ResourceFailure>;
}>()("workerstation/frontend/RecordingScopeIo") {
}
export class RecordingRecentScopes extends Context.Service<RecordingRecentScopes, {
    readonly recent: (workspaceId: string) => Effect.Effect<readonly RecentFlowScope[]>;
    readonly remember: (workspaceId: string, scopeIds: readonly string[]) => Effect.Effect<readonly RecentFlowScope[]>;
}>()("workerstation/frontend/RecordingRecentScopes") {
}
export const makeRecordingRecentScopes = Effect.gen(function* () {
    const io = yield* RecordingScopeIo;
    const readAll = Effect.gen(function* () {
        const raw = yield* io.read.pipe(Effect.orElseSucceed(() => null));
        if (!raw)
            return [];
        const parsed = yield* Effect.try({ try: (): unknown => JSON.parse(raw), catch: () => resourceUnavailable() }).pipe(Effect.orElseSucceed(() => undefined));
        return Array.isArray(parsed) ? parsed.filter(Schema.is(RecentFlowScope)) : [];
    });
    const recent = Effect.fn("Frontend.Recording.scopes.recent")((workspaceId: string) => readAll.pipe(Effect.map(values => values.filter(value => value.workspaceId === workspaceId.toLowerCase()).sort((a, b) => b.seenAt - a.seenAt))));
    const remember = Effect.fn("Frontend.Recording.scopes.remember")((workspaceId: string, scopeIds: readonly string[]) => Effect.gen(function* () {
        const owner = workspaceId.toLowerCase();
        const fresh = [...new Set(scopeIds.map(scope => scope.toLowerCase()).filter(scope => scope && scope !== owner))];
        if (fresh.length === 0)
            return yield* recent(workspaceId);
        const clock = yield* Effect.result(io.now);
        if (clock._tag === "Failure")
            return yield* recent(workspaceId);
        const others = (yield* readAll).filter(value => value.workspaceId !== owner || !fresh.includes(value.scopeId));
        const next = [...fresh.map(scopeId => ({ workspaceId: owner, scopeId, seenAt: clock.success })), ...others];
        const perOwner = new Map<string, number>();
        const trimmed = next.filter(value => {
            const count = perOwner.get(value.workspaceId) ?? 0;
            if (count >= RECENT_FLOW_SCOPE_LIMIT)
                return false;
            perOwner.set(value.workspaceId, count + 1);
            return true;
        });
        yield* io.write(JSON.stringify(trimmed)).pipe(Effect.orElseSucceed(() => undefined));
        return yield* recent(workspaceId);
    }));
    return RecordingRecentScopes.of({ recent, remember });
});
export const RecordingRecentScopesTest = (io: typeof RecordingScopeIo.Service) => Layer.effect(RecordingRecentScopes, makeRecordingRecentScopes).pipe(Layer.provide(Layer.succeed(RecordingScopeIo, io)));
export const recentFlowScopes = (workspaceId: string) => RecordingRecentScopes.use(service => service.recent(workspaceId));
export const rememberFlowScopes = (workspaceId: string, scopeIds: readonly string[]) => RecordingRecentScopes.use(service => service.remember(workspaceId, scopeIds));
